import { useState, useEffect } from "react";
import envConfig from '../envConfig';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Swal from 'sweetalert2';

const DELIVERED_COLOR = '#1A4985';
const FAILED_COLOR    = '#E1A325';

const exportCSV = (filename, headers, rows) => {
	const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
	const lines = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))];
	const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url; a.download = filename; a.click();
	URL.revokeObjectURL(url);
};

const TAB_STYLE = (active) => ({
	borderBottom: active ? '2px solid #1A4985' : '2px solid transparent',
	color: active ? '#1A4985' : '#6c757d',
	borderRadius: 0,
	fontWeight: active ? 600 : 400,
});

function DeliveryDashboard({ getAuthHeaders, handleApiError }) {
	const [provinceOptions, setProvinceOptions] = useState([]);
	const [allDistricts, setAllDistricts]       = useState([]);
	const [districtOptions, setDistrictOptions] = useState([]);
	const [allCamps, setAllCamps]               = useState([]);
	const [campOptions, setCampOptions]         = useState([]);
	const [selectedAdm1, setSelectedAdm1]       = useState('');
	const [selectedAdm2, setSelectedAdm2]       = useState('');
	const [selectedAdm3, setSelectedAdm3]       = useState('');
	const [dashDateFrom, setDashDateFrom]       = useState('');
	const [dashDateTo, setDashDateTo]           = useState('');

	const [activeSubTab, setActiveSubTab]             = useState('dashboard');
	const [activeDataSection, setActiveDataSection]   = useState('errors');

	const [dashData, setDashData]   = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const [reportMeta, setReportMeta] = useState(null);
	const [reportRows, setReportRows] = useState([]);
	const [reportPage, setReportPage] = useState(1);
	const [reportSize, setReportSize] = useState(100);
	const [repDateFrom, setRepDateFrom] = useState('');
	const [repDateTo, setRepDateTo]     = useState('');
	const [repStatus, setRepStatus]     = useState('');
	const [repCampaign, setRepCampaign] = useState('');

	useEffect(() => {
		Promise.all([
			fetch(`${envConfig.API_URL}/lookups/locations/adm1?country_id=2`, { method: 'GET', headers: getAuthHeaders() }),
			fetch(`${envConfig.API_URL}/lookups/locations/adm2?country_id=2`, { method: 'GET', headers: getAuthHeaders() }),
			fetch(`${envConfig.API_URL}/lookups/locations/adm3?country_id=2`, { method: 'GET', headers: getAuthHeaders() }),
		])
			.then(rs => Promise.all(rs.map(r => r.json())))
			.then(([adm1Data, adm2Data, adm3Data]) => {
				setProvinceOptions(adm1Data.data || []);
				setAllDistricts(adm2Data.data || []);
				setAllCamps(adm3Data.data || []);
			})
			.catch(() => {});
	}, []);

	const onAdm1Change = adm1Id => {
		setSelectedAdm1(adm1Id);
		setSelectedAdm2('');
		setSelectedAdm3('');
		setDistrictOptions(adm1Id ? allDistricts.filter(d => d.adm1_id === +adm1Id) : []);
		setCampOptions([]);
	};

	const onAdm2Change = adm2Id => {
		setSelectedAdm2(adm2Id);
		setSelectedAdm3('');
		setCampOptions(adm2Id ? allCamps.filter(c => c.adm2_id === +adm2Id) : []);
	};

	const buildParams = (base = {}) => {
		const p = new URLSearchParams(base);
		if (selectedAdm3) p.append('adm3_id', selectedAdm3);
		else if (selectedAdm2) p.append('adm2_id', selectedAdm2);
		else if (selectedAdm1) p.append('adm1_id', selectedAdm1);
		return p;
	};

	const fetchDashboard = () => {
		setIsLoading(true);
		const dp = buildParams();
		if (dashDateFrom) dp.append('date_from', dashDateFrom);
		if (dashDateTo)   dp.append('date_to',   dashDateTo);
		fetch(`${envConfig.API_URL}/zmb_airtel/dashboard?${dp}`, { method: 'GET', headers: getAuthHeaders() })
			.then(async r => {
				const data = await r.json().catch(() => ({}));
				if (!r.ok) throw { error: new Error(data.message || `HTTP error! status: ${r.status}`), response: r };
				return data;
			})
			.then(data => { if (data.success === 1) setDashData(data.data); })
			.catch(({ error, response }) => {
				const msg = handleApiError(error, response);
				Swal.fire({ title: 'Error!', text: msg || 'Could not load delivery data', icon: 'error', confirmButtonText: 'OK' });
			})
			.finally(() => setIsLoading(false));
	};

	const fetchReport = (pg = 1, sz = reportSize) => {
		setIsLoading(true);
		const p = buildParams({ page: pg, size: sz });
		if (repDateFrom) p.append('date_from', repDateFrom);
		if (repDateTo)   p.append('date_to',   repDateTo);
		if (repStatus)   p.append('message_status', repStatus);
		if (repCampaign) p.append('campaign_id', repCampaign);
		fetch(`${envConfig.API_URL}/zmb_airtel/report?${p}`, { method: 'GET', headers: getAuthHeaders() })
			.then(async r => {
				const data = await r.json().catch(() => ({}));
				if (!r.ok) throw { error: new Error(data.message || `HTTP error! status: ${r.status}`), response: r };
				return data;
			})
			.then(data => {
				if (data.success === 1) {
					setReportMeta(data.metadata);
					setReportRows(data.data);
					setReportPage(pg);
				}
			})
			.catch(({ error, response }) => {
				const msg = handleApiError(error, response);
				Swal.fire({ title: 'Error!', text: msg || 'Could not load report', icon: 'error', confirmButtonText: 'OK' });
			})
			.finally(() => setIsLoading(false));
	};

	// ---- KPI cards ----
	const kpi = dashData?.kpi;
	const kpiCards = kpi ? [
		{ label: 'Total Messages',        value: kpi.total?.toLocaleString() ?? '—' },
		{ label: 'Delivered',             value: kpi.delivered?.toLocaleString() ?? '—' },
		{ label: 'Failed',                value: kpi.failed?.toLocaleString() ?? '—' },
		{ label: 'Delivery Rate',         value: kpi.rate != null ? `${(kpi.rate * 100).toFixed(1)}%` : '—' },
		{ label: 'Unique MSISDNs Failed', value: kpi.unique_failed?.toLocaleString() ?? '—' },
	] : [];

	// ---- Chart config helpers ----
	const base = (type, height = 320) => ({
		chart: { type, height },
		credits: { enabled: false },
		title: { text: null },
		subtitle: { text: null },
		tooltip: { shared: true },
	});

	const mkStacked = (categories, delivered, failed) => ({
		...base('column'),
		xAxis: { categories, crosshair: true },
		yAxis: { min: 0, title: { text: 'Messages' } },
		plotOptions: { column: { stacking: 'normal', borderWidth: 0 } },
		series: [
			{ name: 'Delivered', data: delivered, color: DELIVERED_COLOR },
			{ name: 'Failed',    data: failed,    color: FAILED_COLOR },
		],
	});

	const mkCombo = (categories, failed, rates) => ({
		chart: { height: 320 },
		credits: { enabled: false },
		title: { text: null },
		subtitle: { text: null },
		tooltip: { shared: true },
		xAxis: { categories, crosshair: true },
		yAxis: [
			{ min: 0, title: { text: 'Failed messages' } },
			{ min: 0, max: 100, opposite: true, title: { text: 'Failure rate (%)' }, gridLineWidth: 0 },
		],
		plotOptions: { column: { borderWidth: 0 } },
		series: [
			{ name: 'Failed messages',  type: 'column', data: failed, color: FAILED_COLOR,    yAxis: 0 },
			{ name: 'Failure rate (%)', type: 'line',   data: rates,  color: DELIVERED_COLOR, yAxis: 1, marker: { enabled: true, radius: 3 } },
		],
	});

	// ---- Chart options ----
	const optStatus = kpi ? {
		...base('pie', 280),
		series: [{ name: 'Messages', colorByPoint: true, data: [
			{ name: 'Delivered', y: kpi.delivered, color: DELIVERED_COLOR },
			{ name: 'Failed',    y: kpi.failed,    color: FAILED_COLOR },
		]}],
	} : null;

	const optErrors = dashData?.by_error?.length ? {
		...base('bar', Math.max(280, dashData.by_error.length * 28 + 60)),
		xAxis: { categories: dashData.by_error.map(e => e.error_description || '(blank)'), title: { text: null } },
		yAxis: { min: 0, title: { text: 'Messages' } },
		plotOptions: { bar: { borderWidth: 0 } },
		series: [{ name: 'Messages', data: dashData.by_error.map(e => e.count), color: DELIVERED_COLOR, showInLegend: false }],
	} : null;

	const optCategories = dashData?.by_category?.length ? {
		...base('pie', 280),
		series: [{ name: 'Messages', colorByPoint: true, data: dashData.by_category.map(c => ({ name: c.error_code || '(blank)', y: c.count })) }],
	} : null;

	const optDates  = dashData?.by_date?.length
		? mkStacked(dashData.by_date.map(d => d.date), dashData.by_date.map(d => d.delivered), dashData.by_date.map(d => d.failed))
		: null;

	const optProv   = dashData?.by_province?.length
		? mkStacked(dashData.by_province.map(d => d.label), dashData.by_province.map(d => d.delivered), dashData.by_province.map(d => d.failed))
		: null;

	const optDist   = dashData?.by_district?.length
		? mkStacked(dashData.by_district.map(d => d.label), dashData.by_district.map(d => d.delivered), dashData.by_district.map(d => d.failed))
		: null;

	const optCamp   = dashData?.by_camp?.length
		? mkStacked(dashData.by_camp.map(d => d.label), dashData.by_camp.map(d => d.delivered), dashData.by_camp.map(d => d.failed))
		: null;

	const optFprov  = dashData?.failures_by_province?.length
		? mkCombo(
			dashData.failures_by_province.map(d => d.label),
			dashData.failures_by_province.map(d => d.failed),
			dashData.failures_by_province.map(d => +(d.fail_rate * 100).toFixed(1)),
		) : null;

	const optFdist  = dashData?.failures_by_district?.length
		? mkCombo(
			dashData.failures_by_district.map(d => d.label),
			dashData.failures_by_district.map(d => d.failed),
			dashData.failures_by_district.map(d => +(d.fail_rate * 100).toFixed(1)),
		) : null;

	const optFcamp  = dashData?.failures_by_camp?.length
		? mkCombo(
			dashData.failures_by_camp.map(d => d.label),
			dashData.failures_by_camp.map(d => d.failed),
			dashData.failures_by_camp.map(d => +(d.fail_rate * 100).toFixed(1)),
		) : null;

	const optRisk   = dashData?.by_risk?.length ? {
		...base('column'),
		xAxis: { categories: dashData.by_risk.map(d => d.label), crosshair: true },
		yAxis: { min: 0, title: { text: 'Messages' } },
		plotOptions: { column: { stacking: 'normal', borderWidth: 0 } },
		series: [
			{ name: 'Delivered', data: dashData.by_risk.map(d => d.delivered), color: DELIVERED_COLOR },
			{ name: 'Failed',    data: dashData.by_risk.map(d => d.failed),    color: FAILED_COLOR },
		],
	} : null;

	const CHARTS = [
		{ key: 'status',     label: '1. Message Status Summary',                   opts: optStatus,     col: 'col-sm-12 col-lg-6' },
		{ key: 'errors',     label: '2a. Delivery Errors by Description',           opts: optErrors,     col: 'col-sm-12 col-lg-6' },
		{ key: 'categories', label: '2b. Delivery Errors by Category',              opts: optCategories, col: 'col-sm-12 col-lg-6' },
		{ key: 'dates',      label: '3. Date-wise: Delivered vs Failed',            opts: optDates,      col: 'col-sm-12 col-lg-6' },
		{ key: 'prov',       label: '4a. Province-wise: Delivered vs Failed',       opts: optProv,       col: 'col-sm-12 col-lg-6' },
		{ key: 'dist',       label: '4b. District-wise: Delivered vs Failed',       opts: optDist,       col: 'col-sm-12 col-lg-6' },
		{ key: 'camp',       label: '4c. Camp-wise: Delivered vs Failed (Top 15)',  opts: optCamp,       col: 'col-sm-12 col-lg-12' },
		{ key: 'fprov',      label: '5a. Province-wise Failures',                   opts: optFprov,      col: 'col-sm-12 col-lg-6' },
		{ key: 'fdist',      label: '5b. District-wise Failures',                   opts: optFdist,      col: 'col-sm-12 col-lg-6' },
		{ key: 'fcamp',      label: '5c. Camp-wise Failures (Top 15)',              opts: optFcamp,      col: 'col-sm-12 col-lg-12' },
		{ key: 'rlevel',     label: '7. Risk Level vs Delivery',                    opts: optRisk,       col: 'col-sm-12 col-lg-12' },
	];

	// ---- Data table sections ----
	const dataSections = dashData ? [
		{ key: 'errors',     label: '2a. Errors by Description',   headers: ['Error Description', 'Count'],                 rows: dashData.by_error?.map(e => [e.error_description || '(blank)', e.count]) },
		{ key: 'categories', label: '2b. Errors by Category',      headers: ['Error Code', 'Count'],                        rows: dashData.by_category?.map(c => [c.error_code || '(blank)', c.count]) },
		{ key: 'dates',      label: '3. Date-wise',                 headers: ['Date', 'Delivered', 'Failed'],                rows: dashData.by_date?.map(d => [d.date, d.delivered, d.failed]) },
		{ key: 'prov',       label: '4a. Province-wise',            headers: ['Province', 'Delivered', 'Failed'],            rows: dashData.by_province?.map(d => [d.label, d.delivered, d.failed]) },
		{ key: 'dist',       label: '4b. District-wise',            headers: ['District', 'Delivered', 'Failed'],            rows: dashData.by_district?.map(d => [d.label, d.delivered, d.failed]) },
		{ key: 'camp',       label: '4c. Camp-wise (Top 15)',       headers: ['Camp', 'Delivered', 'Failed'],                rows: dashData.by_camp?.map(d => [d.label, d.delivered, d.failed]) },
		{ key: 'fprov',      label: '5a. Province Failures',        headers: ['Province', 'Failed', 'Failure Rate'],         rows: dashData.failures_by_province?.map(d => [d.label, d.failed, `${(d.fail_rate * 100).toFixed(1)}%`]) },
		{ key: 'fdist',      label: '5b. District Failures',        headers: ['District', 'Failed', 'Failure Rate'],         rows: dashData.failures_by_district?.map(d => [d.label, d.failed, `${(d.fail_rate * 100).toFixed(1)}%`]) },
		{ key: 'fcamp',      label: '5c. Camp Failures (Top 15)',   headers: ['Camp', 'Failed', 'Failure Rate'],             rows: dashData.failures_by_camp?.map(d => [d.label, d.failed, `${(d.fail_rate * 100).toFixed(1)}%`]) },
		{ key: 'rlevel',     label: '7. Risk Level vs Delivery',    headers: ['Risk Level', 'Delivered', 'Failed'],          rows: dashData.by_risk?.map(d => [d.label, d.delivered, d.failed]) },
	] : [];
	const activeSection = dataSections.find(s => s.key === activeDataSection);

	return (
		<div>
			{/* ---- Delivery filter bar ---- */}
			<div className="d-flex flex-wrap align-items-end gap-3 mb-3">
				<div>
					<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Date From</label>
					<input type="date" className="form-control" value={dashDateFrom} onChange={e => setDashDateFrom(e.target.value)} />
				</div>
				<div>
					<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Date To</label>
					<input type="date" className="form-control" value={dashDateTo} onChange={e => setDashDateTo(e.target.value)} />
				</div>
				<div>
					<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Province</label>
					<select className="form-select h40px" value={selectedAdm1} onChange={e => onAdm1Change(e.target.value)}>
						<option value="">All provinces</option>
						{provinceOptions.map(p => (
							<option key={p.adm1_id} value={p.adm1_id}>{p.adm1}</option>
						))}
					</select>
				</div>
				<div>
					<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>District</label>
					<select className="form-select h40px" value={selectedAdm2} onChange={e => onAdm2Change(e.target.value)} disabled={!selectedAdm1}>
						<option value="">All districts</option>
						{districtOptions.map(d => (
							<option key={d.adm2_id} value={d.adm2_id}>{d.adm2}</option>
						))}
					</select>
				</div>
				<div>
					<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Camp</label>
					<select className="form-select h40px" value={selectedAdm3} onChange={e => setSelectedAdm3(e.target.value)} disabled={!selectedAdm2}>
						<option value="">All camps</option>
						{campOptions.map(c => (
							<option key={c.adm3_id} value={c.adm3_id}>{c.adm3}</option>
						))}
					</select>
				</div>
				<div className="mt-3 pt-1">
					<button className="btn btn-applyNew" style={{ width: 'auto' }} onClick={fetchDashboard}>
						Apply
					</button>
				</div>
			</div>

			{/* ---- Sub-tab bar ---- */}
			<div className="d-flex mb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
				{[
					{ key: 'dashboard', label: 'Dashboard' },
					{ key: 'data',      label: 'Data Tables' },
					{ key: 'report',    label: 'Final Report' },
				].map(t => (
					<button
						key={t.key}
						className="btn btn-link text-decoration-none px-3 py-2"
						style={TAB_STYLE(activeSubTab === t.key)}
						onClick={() => {
							setActiveSubTab(t.key);
							if (t.key === 'report' && !reportMeta) fetchReport(1);
						}}
					>
						{t.label}
					</button>
				))}
			</div>

			{/* ============ Dashboard sub-tab ============ */}
			{activeSubTab === 'dashboard' && (
				<>
					{!dashData ? (
						<div className="text-center text-muted my-5 py-5">
							Select a sheet and click <strong>Apply</strong> to load delivery data
						</div>
					) : (
						<>
							{/* KPI cards */}
							<div className="row mb-3">
								{kpiCards.map((k, i) => (
									<div key={i} className="col-6 col-md-4 col-lg-2 mb-2">
										<div className="card h-100">
											<div className="card-body text-center py-3">
												<div className="fw-bold" style={{ fontSize: '1.5rem', color: DELIVERED_COLOR }}>{k.value}</div>
												<div className="text-muted" style={{ fontSize: '12px' }}>{k.label}</div>
											</div>
										</div>
									</div>
								))}
							</div>

							{/* Chart grid */}
							<div className="row">
								{CHARTS.map(c => c.opts && (
									<div key={c.key} className={`${c.col} mb-3`}>
										<div className="card h-100">
											<div className="card-body">
												<h6 className="chartTitle mb-3">{c.label}</h6>
												<HighchartsReact highcharts={Highcharts} options={c.opts} />
											</div>
										</div>
									</div>
								))}
							</div>
						</>
					)}
				</>
			)}

			{/* ============ Data Tables sub-tab ============ */}
			{activeSubTab === 'data' && (
				<>
					{!dashData ? (
						<div className="text-center text-muted my-5 py-5">
							Apply filters on the Dashboard sub-tab first to load data
						</div>
					) : (
						<>
							{/* Chip nav */}
							<div className="d-flex flex-wrap gap-2 mb-3">
								{dataSections.map(s => (
									<button
										key={s.key}
										className="btn btn-sm"
										style={{
											background: activeDataSection === s.key ? DELIVERED_COLOR : '#f0f0f0',
											color: activeDataSection === s.key ? '#fff' : '#333',
											border: 'none',
											borderRadius: '16px',
											fontSize: '12px',
										}}
										onClick={() => setActiveDataSection(s.key)}
									>
										{s.label}
									</button>
								))}
							</div>

							{/* Active table */}
							{activeSection && (
								<div className="card">
									<div className="card-body">
										<div className="d-flex justify-content-between align-items-center mb-3">
											<h6 className="chartTitle mb-0">{activeSection.label}</h6>
											<button
												className="btn btn-sm"
												style={{ background: '#f0f0f0', border: 'none', fontSize: '12px' }}
												onClick={() => exportCSV(`${activeSection.key}.csv`, activeSection.headers, activeSection.rows || [])}
											>
												Export CSV
											</button>
										</div>
										<div className="table-responsive">
											<table className="table tbl_new tdColor table-bordered" style={{ fontSize: '13px' }}>
												<thead>
													<tr>
														{activeSection.headers.map((h, i) => (
															<th key={i}><strong>{h}</strong></th>
														))}
													</tr>
												</thead>
												<tbody>
													{(activeSection.rows || []).length > 0
														? (activeSection.rows || []).map((row, i) => (
															<tr key={i}>
																{row.map((cell, j) => (
																	<td key={j} style={{ textAlign: typeof cell === 'number' ? 'right' : 'left' }}>
																		{typeof cell === 'number' ? cell.toLocaleString() : cell}
																	</td>
																))}
															</tr>
														))
														: <tr><td colSpan={activeSection.headers.length} className="text-center text-muted">No data</td></tr>
													}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</>
			)}

			{/* ============ Final Report sub-tab ============ */}
			{activeSubTab === 'report' && (
				<>
					{/* Report-specific filters */}
					<div className="d-flex flex-wrap align-items-end gap-3 mb-3">
						<div>
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Date From</label>
							<input type="date" className="form-control" value={repDateFrom} onChange={e => setRepDateFrom(e.target.value)} />
						</div>
						<div>
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Date To</label>
							<input type="date" className="form-control" value={repDateTo} onChange={e => setRepDateTo(e.target.value)} />
						</div>
						<div>
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Message Status</label>
							<select className="form-select h40px" value={repStatus} onChange={e => setRepStatus(e.target.value)}>
								<option value="">All statuses</option>
								<option value="Delivered">Delivered</option>
								<option value="Failed">Failed</option>
							</select>
						</div>
						<div>
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Campaign ID</label>
							<input type="text" className="form-control" placeholder="Campaign ID" value={repCampaign} onChange={e => setRepCampaign(e.target.value)} />
						</div>
						<div>
							<label className="fw-semibold fs-6" style={{ whiteSpace: 'nowrap' }}>Rows per page</label>
							<select
								className="form-select h40px"
								value={reportSize}
								onChange={e => { const sz = +e.target.value; setReportSize(sz); fetchReport(1, sz); }}
							>
								<option value={50}>50</option>
								<option value={100}>100</option>
								<option value={250}>250</option>
							</select>
						</div>
						<div className="mt-3 pt-1">
							<button className="btn btn-applyNew" style={{ width: 'auto' }} onClick={() => fetchReport(1)}>
								Apply
							</button>
						</div>
					</div>

					{/* Pagination row */}
					{reportMeta && (
						<div className="d-flex justify-content-between align-items-center mb-2">
							<span className="text-muted" style={{ fontSize: '13px' }}>
								{reportMeta.total_count.toLocaleString()} records &mdash; Page {reportMeta.page} / {reportMeta.total_pages}
							</span>
							<div className="d-flex gap-2">
								<button
									className="btn btn-sm"
									style={{ background: '#f0f0f0', border: 'none', fontSize: '12px' }}
									onClick={() => {
										const headers = ['Province', 'District', 'Camp', 'MSISDN', 'Delivered Time', 'Sender ID', 'Campaign Name', 'Message Status', 'Error Code', 'Error Description'];
										const rows = reportRows.map(r => [r.adm1, r.adm2, r.adm3, r.msisdn, r.delivered_time, r.sender_id, r.campaign_name, r.message_status, r.error_code, r.error_description]);
										exportCSV(`final_report_page${reportPage}.csv`, headers, rows);
									}}
								>
									Export CSV
								</button>
								<button
									className="btn btn-sm me-1"
									style={{ background: '#f0f0f0', border: 'none' }}
									disabled={reportPage <= 1}
									onClick={() => fetchReport(reportPage - 1)}
								>
									‹ Prev
								</button>
								<button
									className="btn btn-sm"
									style={{ background: '#f0f0f0', border: 'none' }}
									disabled={reportPage >= reportMeta.total_pages}
									onClick={() => fetchReport(reportPage + 1)}
								>
									Next ›
								</button>
							</div>
						</div>
					)}

					{/* Report table */}
					<div className="table-responsive">
						<table className="table tbl_new tdColor table-bordered" style={{ fontSize: '12px' }}>
							<thead>
								<tr>
									<th><strong>Province</strong></th>
									<th><strong>District</strong></th>
									<th><strong>Camp</strong></th>
									<th><strong>MSISDN</strong></th>
									<th><strong>Delivered Time</strong></th>
									<th><strong>Sender ID</strong></th>
									<th><strong>Campaign Name</strong></th>
									<th><strong>Message Status</strong></th>
									<th><strong>Error Code</strong></th>
									<th><strong>Error Description</strong></th>
								</tr>
							</thead>
							<tbody>
								{reportRows.length > 0
									? reportRows.map((row, i) => (
										<tr key={i}>
											<td>{row.adm1 || '—'}</td>
											<td>{row.adm2 || '—'}</td>
											<td>{row.adm3 || '—'}</td>
											<td>{row.msisdn}</td>
											<td>{row.delivered_time || '—'}</td>
											<td>{row.sender_id || '—'}</td>
											<td>{row.campaign_name || '—'}</td>
											<td>{row.message_status || '—'}</td>
											<td>{row.error_code || '—'}</td>
											<td>{row.error_description || '—'}</td>
										</tr>
									))
									: (
										<tr>
											<td colSpan={10} className="text-center text-muted">
												{reportMeta ? 'No records match the selected filters' : 'Apply filters to load report'}
											</td>
										</tr>
									)
								}
							</tbody>
						</table>
					</div>
				</>
			)}

			{/* Loader */}
			{isLoading && (
				<div
					style={{
						position: 'fixed', inset: 0,
						background: 'rgba(0,0,0,0.75)',
						zIndex: 2000,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
					}}
				>
					<div className="spinner-border text-light" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			)}
		</div>
	);
}

export default DeliveryDashboard;
